if (!customElements.get('image-switch-banner')) {
  class ImageSwitchBanner extends HTMLElement {
    constructor() {
      super();

      this.wrapper = this.querySelector('[data-wrapper]');
      this.imageSecondary = this.querySelector('[data-media-secondary]');
      this.header = document.querySelector(
        '#shopify-section-header header, .shopify-section-group-header-group header',
      );
    }

    connectedCallback() {
      if (!this.imageSecondary) return;

      // Lock scroll and force the page to start at the top
      window.scrollTo(0, 0);
      document.documentElement.classList.add('page-loading');

      window.addEventListener('load', () => {
        gsap.registerPlugin(ScrollTrigger);
        ScrollTrigger.config({ ignoreMobileResize: true });
        ScrollTrigger.normalizeScroll(true);

        this.init();

        // Unlock scroll after GSAP has initialised and painted the first frame
        requestAnimationFrame(() => {
          document.documentElement.classList.remove('page-loading');
        });
      });
    }

    init() {
      const rect = this.imageSecondary.getBoundingClientRect();
      const maxRadius =
        (Math.sqrt(rect.width ** 2 + rect.height ** 2) / 2) * 1.05;
      // Progress value at which the circle physically reaches the bottom edge
      const tBottomEdge = rect.height / 2 / maxRadius;
      const state = { radius: 0 };

      // Parse the two background colours for gradient interpolation
      const cs = getComputedStyle(this);
      const parseHex = (hex) => {
        const h = hex.trim().replace('#', '');
        return [
          parseInt(h.slice(0, 2), 16),
          parseInt(h.slice(2, 4), 16),
          parseInt(h.slice(4, 6), 16),
        ];
      };
      const c1 = parseHex(cs.getPropertyValue('--color-background'));
      const c2 = parseHex(cs.getPropertyValue('--color-background-circle'));

      const setMask = (radius) => {
        const r = Math.max(radius, 0.1);
        const r0 = r * 0.65;
        const r1 = r * 0.82;
        const r2 = r * 0.94;
        const mask = `radial-gradient(circle ${r}px at 50% 50%, black 0%, black ${r0}px, rgba(0,0,0,0.85) ${r1}px, rgba(0,0,0,0.3) ${r2}px, transparent ${r}px)`;
        this.imageSecondary.style.maskImage = mask;
        this.imageSecondary.style.webkitMaskImage = mask;
        this.imageSecondary.style.opacity = Math.min(
          1,
          (radius / maxRadius) * 2,
        );

        // Only change bottom color/shapes once the circle has reached the bottom edge
        const t = Math.min(1, radius / maxRadius);
        const tShapes =
          t < tBottomEdge
            ? 0
            : Math.min(1, (t - tBottomEdge) / (1 - tBottomEdge));
        const ri = Math.round(c1[0] + (c2[0] - c1[0]) * tShapes);
        const gi = Math.round(c1[1] + (c2[1] - c1[1]) * tShapes);
        const bi = Math.round(c1[2] + (c2[2] - c1[2]) * tShapes);
        this.wrapper.style.setProperty(
          '--color-gradient-bottom',
          `rgb(${ri},${gi},${bi})`,
        );
        this.wrapper.style.setProperty(
          '--color-background',
          `rgb(${ri},${gi},${bi})`,
        );
      };

      setMask(0);

      const CIRCLE_DURATION = 0.7;

      // Shorter scroll distance on mobile so the reveal completes faster.
      // Desktop keeps the original, longer distance.
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const scrollEnd = isMobile ? '+=100%' : '+=175%';

      // Lock the header's scroll state so global.js doesn't add `is-scrolled`
      // partway through the animation. We release control on onLeave.
      if (this.header) this.header.dataset.scrollStateLocked = '';

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: this.wrapper,
          start: 'top top',
          end: scrollEnd,
          scrub: 0.5,
          pin: this.wrapper,
          pinSpacing: true,
          anticipatePin: 1,
          pinType: 'transform',
          snap: {
            snapTo: (value) => (value > 0.75 ? 1 : value),
            duration: { min: 0.5, max: 1.0 },
            ease: 'power2.inOut',
            delay: 0.1,
          },
          onLeave: () => {
            if (!this.header) return;
            // Animation done — hand scroll-state control back to global.js
            delete this.header.dataset.scrollStateLocked;
            this.header.classList.add('is-scrolled');
          },
          onEnterBack: () => {
            if (!this.header) return;
            // User scrolled back into the hero — re-lock and go transparent
            this.header.dataset.scrollStateLocked = '';
            this.header.classList.remove('is-scrolled');
          },
        },
      });

      tl.to(state, {
        radius: maxRadius,
        ease: 'none',
        duration: CIRCLE_DURATION,
        onUpdate: () => setMask(state.radius),
      });
    }
  }

  customElements.define('image-switch-banner', ImageSwitchBanner);
}
